import { TRootState } from 'src/app/store';
import { makeReduxDuck } from 'teedux';
import {
    ABOUT,
    ATTRIBUTES,
    BOOKMARKS_LIST,
    DEVICE,
    EDIT_MONITORED_OBJECT,
    EMPLOYEE,
    EMPLOYEE_RFID,
    FUEL_TANK,
    GRID_VIEW,
    LIVE_VIEW,
    LOGIN_MIGRATION,
    OBJECT_RECALCULATION,
    PLAYER_SETTINGS,
    PRODUCT_ASSIGNMENT,
    REGISTER_VEHICLE,
    REGISTER_VEHICLE_RFID,
    RFID_CARD,
    ROUTES_MANAGEMENT_TASKS_ASSIGN_CONFIRMATION,
    SEND_RFID_CARDS,
    TASK_ID_ROUTES,
    TASK_MODIFICATION,
    TICKET_HISTORY_GRID,
    USER_EDITION,
    USER_PROFILE,
} from '../../../constants/dictionaries/GlobalDialogTypes';
import { IPlayerSettings } from '../discoverySlice';
import { ITaskGridDialogFilter } from '../../../components/globalDialog/components/TaskGridDialog';
import { IHeaderData } from '../../../components/globalDialog/components/EditObjectDialog/types';
import { ITaskModificationDialogParams } from '../../../components/globalDialog/components/TaskModificationDialog/types';
import { IDevice, IReportEntity } from '../../types';
import { createSelector } from '@reduxjs/toolkit';
import { IFuelTank } from '../../../components/registers/FuelTanks/types';
import { IProduct } from '../../../features/Products/types';
import { IUser } from '@/app/state/app/collections/dataTypes';
import { TDeviceDialogType } from '@/app/components/globalDialog/components/device/types';

type TDialogType =
    | 'FUEL_TANK'
    | 'EMPLOYEE'
    | 'EMPLOYEE_RFID'
    | 'REGISTER_VEHICLE'
    | 'REGISTER_VEHICLE_RFID'
    | 'RFID_CARD'
    | 'SEND_RFID_CARDS'
    | 'ABOUT'
    | 'USER_PROFILE'
    | 'BOOKMARKS_LIST';

interface IDialog {
    dialogType: TDialogType;
    dialogData: any;
    visible: boolean;
}

interface IState {
    dialogs: IDialog[];
}

export const initialState: IState = {
    dialogs: [],
};

const duck = makeReduxDuck('ui/groups', initialState);

export const pushDialog = duck.defineAction<any>(
    'PUSH_DIALOG',
    (state, dialog) => ({
        dialogs: [...state.dialogs, { ...dialog, visible: true }],
    })
);
export const hideTopDialog = duck.definePayloadlessAction(
    'HIDE_TOP_DIALOG',
    (state) => {
        const dialogs = [...state.dialogs];
        if (dialogs.length > 0) {
            dialogs[dialogs.length - 1] = {
                ...dialogs[dialogs.length - 1],
                visible: false,
            };
        }
        return { dialogs };
    }
);

export const showTopDialog = duck.definePayloadlessAction(
    'SHOW_TOP_DIALOG',
    (state) => {
        const dialogs = [...state.dialogs];
        if (dialogs.length > 0) {
            dialogs[dialogs.length - 1] = {
                ...dialogs[dialogs.length - 1],
                visible: true,
            };
        }
        return { dialogs };
    }
);

export const popDialog = duck.definePayloadlessAction(
    'POP_DIALOG',
    (state) => ({
        dialogs: state.dialogs.slice(0, -1),
    })
);

export const showFuelTankDialog = (dialogData: { fuelTank: IFuelTank }) =>
    pushDialog({ dialogData, dialogType: FUEL_TANK });

export const showEmployeeDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: EMPLOYEE });

export const showEmployeeRFIDDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: EMPLOYEE_RFID });

export const showVehicleDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: REGISTER_VEHICLE });

export const showVehicleRFIDDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: REGISTER_VEHICLE_RFID });

export const showRFIDCardDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: RFID_CARD });

export const showSendRFIDCardsDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: SEND_RFID_CARDS });

export const showAboutDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: ABOUT });

export const showUserProfileDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: USER_PROFILE });

export const showBookmarskListDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: BOOKMARKS_LIST });

export const showLoginMigrationDialog = (dialogData: any) =>
    pushDialog({ dialogData, dialogType: LOGIN_MIGRATION });

export const showGridViewDialog = (dialogData: {
    id?: string;
    sourceSet: string;
}) => pushDialog({ dialogData, dialogType: GRID_VIEW });

export const showAttributesDialog = (dialogData: { id?: string }) =>
    pushDialog({ dialogData, dialogType: ATTRIBUTES });

export const showRoutesAssignDialog = (dialogData: {
    tasks: { tasksList: { routeName: string; count: number }[]; ids: number[] };
    target: { name: string; id: number };
    warning: boolean;
}) =>
    pushDialog({
        dialogData,
        dialogType: ROUTES_MANAGEMENT_TASKS_ASSIGN_CONFIRMATION,
    });
export const showPlayerSettingsDialog = (dialogData: {
    handleChange: any;
    playerSettings?: IPlayerSettings;
}) => pushDialog({ dialogData, dialogType: PLAYER_SETTINGS });

export const showLiveViewDialog = (dialogData: {
    handleChange: any;
    monitoredId: string;
}) => pushDialog({ dialogData, dialogType: LIVE_VIEW });

export const showTaskRoutesDialog = (dialogData: {
    filter: ITaskGridDialogFilter;
    routeId?: number;
    setFieldValue: (field: string, value: any) => void;
}) => pushDialog({ dialogData, dialogType: TASK_ID_ROUTES });

export const showEditMonitoredObjectDialog = (dialogData: {
    headerData?: IHeaderData;
    onSuccess: () => void;
}) => pushDialog({ dialogData, dialogType: EDIT_MONITORED_OBJECT });

export const showTaskModificationDialog = (
    dialogData: ITaskModificationDialogParams
) => pushDialog({ dialogData, dialogType: TASK_MODIFICATION });

export const showObjectRecalculationDialog = (dialogData: {
    data: IReportEntity;
    monitoredId: string;
    dateFormat: string;
}) => pushDialog({ dialogData, dialogType: OBJECT_RECALCULATION });

export const showProductAssignmentDialog = (dialogData: {
    selectedProduct: IProduct;
}) => pushDialog({ dialogData, dialogType: PRODUCT_ASSIGNMENT });
export const showTicketHistoryGridDialog = (dialogData: { ticketId: string }) =>
    pushDialog({ dialogData, dialogType: TICKET_HISTORY_GRID });

export const showDeviceDialog = (dialogData: {
    mode: TDeviceDialogType;
    device?: IDevice;
}) => pushDialog({ dialogData, dialogType: DEVICE });
export const showUserEditionDialog = (dialogData: {
    selectedUser: IUser;
    onSave: () => void;
}) => pushDialog({ dialogData, dialogType: USER_EDITION });

export const hideGlobalDialog = () => popDialog();
const getDialogs = (state: TRootState) => state.ui.globalDialog.dialogs;
export const getVisibleDialogs = createSelector([getDialogs], (dialogs) =>
    dialogs.filter((d) => d.visible)
);

export default duck.getReducer();
